import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { BGM_AUTH_HEADER } from '../common/constants';
import { ErrorCode } from '../common/enum/error-code.enum';
import { AppContext, type ToastType } from './components/app-context';
import { Button } from './components/button';
import { useStore } from './hooks/use-store';
import { getLocale, setLocale, t, type Locale, type MessageKey } from './i18n';
import { AppLayout, type AppRoute } from './layouts/app.layout';
import { LoginPage } from './pages/login.page';
import { registerPwa } from './pwa';
import {
  $accessToken,
  $bangumiTokenStatus,
  $currentUser,
  $theme,
  clearSession,
  configureUserConfigPersistence,
  hydrateUserConfig,
} from './store/app.store';
import type { CurrentUser, SettingsSection } from './types/page';

interface ApiEnvelope<T> {
  code: number;
  message: string;
  data: T | null;
}

class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly code: number,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

type BangumiRequestMode = 'authenticated' | 'public';

const isApiEnvelope = (value: unknown): value is ApiEnvelope<unknown> =>
  typeof value === 'object' &&
  value !== null &&
  'code' in value &&
  typeof value.code === 'number' &&
  'message' in value &&
  typeof value.message === 'string' &&
  'data' in value;

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: { label: string; run: () => void };
  persistent?: boolean;
}

const apiErrorMessages: Partial<Record<number, MessageKey>> = {
  [ErrorCode.ROUTE_NOT_FOUND]: 'routeNotFound',
  [ErrorCode.REQUEST_VALIDATION_FAILED]: 'requestValidationFailed',
  [ErrorCode.INVALID_JSON_BODY]: 'invalidRequestBody',
  [ErrorCode.INTERNAL_SERVER_ERROR]: 'internalServerError',
  [ErrorCode.WRONG_USERNAME_OR_PASSWORD]: 'wrongUsernameOrPassword',
  [ErrorCode.INVALID_OR_EXPIRED_ACCESS_TOKEN]: 'invalidSession',
  [ErrorCode.USER_NOT_FOUND]: 'userNotFound',
  [ErrorCode.MISSING_ACCESS_TOKEN]: 'authenticationRequired',
  [ErrorCode.INVALID_AUTHORIZATION_HEADER]: 'invalidAuthorization',
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: 'insufficientPermissions',
  [ErrorCode.USER_ALREADY_EXISTS]: 'userAlreadyExists',
  [ErrorCode.CANNOT_DELETE_CURRENT_USER]: 'cannotDeleteCurrentUser',
  [ErrorCode.WRONG_CURRENT_PASSWORD]: 'wrongCurrentPassword',
  [ErrorCode.BGM_API_REQUEST_FAILED]: 'bangumiRequestFailed',
  [ErrorCode.BGM_TOKEN_REQUIRED]: 'bangumiTokenRequired',
  [ErrorCode.BGM_TOKEN_INVALID]: 'bangumiTokenInvalid',
  [ErrorCode.DOWNLOAD_NOT_FOUND]: 'downloadNotFound',
  [ErrorCode.DOWNLOAD_FAILED]: 'downloadFailed',
  [ErrorCode.DOWNLOAD_FILE_NOT_FOUND]: 'downloadFileNotFound',
  [ErrorCode.INVALID_RANGE]: 'invalidRange',
  [ErrorCode.DOWNLOAD_METADATA_TIMEOUT]: 'torrentMetadataTimeout',
  [ErrorCode.PLAYBACK_CHANNEL_NOT_FOUND]: 'playbackChannelNotFound',
  [ErrorCode.WEBSOCKET_UPGRADE_FAILED]: 'websocketUpgradeFailed',
  [ErrorCode.MEDIA_PROVIDER_NOT_FOUND]: 'mediaProviderNotFound',
  [ErrorCode.MEDIA_PROVIDER_ALREADY_EXISTS]: 'mediaProviderAlreadyExists',
  [ErrorCode.MEDIA_PROVIDER_TEMPLATE_INVALID]: 'mediaProviderTemplateInvalid',
  [ErrorCode.MEDIA_PROVIDER_REQUEST_FAILED]: 'mediaProviderRequestFailed',
  [ErrorCode.MEDIA_RESOURCE_NOT_FOUND]: 'mediaResourceNotFound',
  [ErrorCode.MEDIA_RESOURCE_EXPIRED]: 'mediaResourceExpired',
  [ErrorCode.AI_SETTINGS_NOT_CONFIGURED]: 'aiServiceNotConfigured',
  [ErrorCode.AI_REQUEST_FAILED]: 'aiRequestFailed',
  [ErrorCode.AI_RESPONSE_INVALID]: 'aiResponseInvalid',
  [ErrorCode.UPDATE_CHECK_FAILED]: 'updateCheckFailed',
};

const readRoute = (): AppRoute => {
  const subject = location.pathname.match(/^\/subjects\/(\d+)\/?$/);
  if (subject?.[1]) {
    return { view: 'subject', subjectId: Number(subject[1]) };
  }
  if (location.pathname === '/schedule') {
    return { view: 'schedule' };
  }
  if (location.pathname === '/collection-records') {
    return { view: 'collectionRecords' };
  }
  const settings = location.pathname.match(/^\/settings(?:\/([^/]+))?\/?$/);
  if (settings) {
    const section = settings[1];
    const sections: SettingsSection[] = [
      'about',
      'account',
      'ai',
      'appearance',
      'downloads',
      'subscriptions',
      'playback',
      'users',
    ];
    return {
      view: 'settings',
      section: sections.includes(section as SettingsSection) ? (section as SettingsSection) : undefined,
    };
  }
  if (location.pathname === '/playback') {
    return { view: 'playback' };
  }
  if (location.pathname === '/search') {
    return { view: 'search', query: new URLSearchParams(location.search).get('q') ?? '' };
  }
  return { view: 'home' };
};

const App = () => {
  const accessToken = useStore($accessToken);
  const currentUser = useStore($currentUser);
  const theme = useStore($theme);
  const [locale, setLocaleState] = useState<Locale>(getLocale());
  const [route, setRoute] = useState<AppRoute>(readRoute);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const authenticationRef = useRef<{ accessToken: string; promise: Promise<void> } | undefined>(undefined);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  useEffect(() => {
    const update = (): void => setRoute(readRoute());
    addEventListener('popstate', update);
    return () => removeEventListener('popstate', update);
  }, []);
  const showToast = useCallback((message: string, type: ToastType = 'default') => {
    const id = Date.now() + Math.random();
    setToasts((items) => [...items, { id, message, type }]);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts((items) => items.filter((item) => item.id !== id));
  }, []);
  const showPwaUpdate = useCallback((worker: ServiceWorker) => {
    const id = Date.now() + Math.random();
    setToasts((items) => [
      ...items.filter((item) => !item.persistent),
      {
        id,
        message: t('pwaUpdateAvailable'),
        type: 'default',
        persistent: true,
        action: { label: t('updateNow'), run: () => worker.postMessage({ type: 'SKIP_WAITING' }) },
      },
    ]);
  }, []);
  useEffect(() => registerPwa(showPwaUpdate), [showPwaUpdate]);
  const logout = useCallback(() => {
    clearSession();
    setRoute({ view: 'home' });
  }, []);
  const request = useCallback(
    async <T,>(path: string, init: RequestInit = {}, bangumiMode?: BangumiRequestMode): Promise<T> => {
      const headers = new Headers(init.headers);
      headers.set('Accept', 'application/json');
      if (init.body && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
      }
      const token = $accessToken.get();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (bangumiMode === 'authenticated') {
        headers.set(BGM_AUTH_HEADER, 'true');
      }
      let response: Response;
      try {
        response = await fetch(bangumiMode ? `/api/bgm${path}` : path, { ...init, headers });
      } catch {
        throw new Error(t('networkError'));
      }
      if (bangumiMode) {
        if (!response.ok) {
          const responseBody = await response.json().catch(() => undefined);
          if (isApiEnvelope(responseBody)) {
            if (responseBody.code === ErrorCode.INVALID_OR_EXPIRED_ACCESS_TOKEN && token) {
              logout();
            } else if (responseBody.code === ErrorCode.BGM_TOKEN_REQUIRED) {
              $bangumiTokenStatus.set('missing');
            } else if (responseBody.code === ErrorCode.BGM_TOKEN_INVALID) {
              $bangumiTokenStatus.set('invalid');
            }
            const key = apiErrorMessages[responseBody.code];
            throw new ApiRequestError(key ? t(key) : t('bangumiRequestFailed'), responseBody.code, response.status);
          }
          throw new Error(`${t('bangumiRequestFailed')} (${response.status})`);
        }
        const responseBody = await response.text();
        if (!responseBody.trim()) {
          return undefined as T;
        }
        return JSON.parse(responseBody) as T;
      }
      const result = (await response.json()) as ApiEnvelope<T>;
      if (!response.ok || result.code !== 0) {
        if (response.status === 401 && token) {
          logout();
        }
        const key = apiErrorMessages[result.code];
        throw new ApiRequestError(key ? t(key) : t('requestFailed'), result.code, response.status);
      }
      return result.data as T;
    },
    [logout],
  );
  const api = useCallback(<T,>(path: string, init?: RequestInit) => request<T>(path, init), [request]);
  const bgm = useCallback(<T,>(path: string, init?: RequestInit) => request<T>(path, init, 'public'), [request]);
  const bgmAuth = useCallback(
    <T,>(path: string, init?: RequestInit) => request<T>(path, init, 'authenticated'),
    [request],
  );
  const checkBangumiToken = useCallback(async (): Promise<void> => {
    const user = $currentUser.get();
    if (!user) {
      $bangumiTokenStatus.set('unchecked');
      return;
    }
    if (!user.bgmToken) {
      $bangumiTokenStatus.set('missing');
      return;
    }
    const token = user.bgmToken;
    $bangumiTokenStatus.set('checking');
    try {
      await bgmAuth('/v0/me');
      if ($currentUser.get()?.bgmToken === token) {
        $bangumiTokenStatus.set('valid');
      }
    } catch (reason) {
      if ($currentUser.get()?.bgmToken !== token) {
        return;
      }
      $bangumiTokenStatus.set(
        reason instanceof ApiRequestError
          ? reason.code === ErrorCode.BGM_TOKEN_INVALID
            ? 'invalid'
            : reason.code === ErrorCode.BGM_TOKEN_REQUIRED
              ? 'missing'
              : 'unavailable'
          : 'unavailable',
      );
    }
  }, [bgmAuth]);
  const authenticate = useCallback((): Promise<void> => {
    const accessToken = $accessToken.get();
    if (authenticationRef.current?.accessToken === accessToken) {
      return authenticationRef.current.promise;
    }
    const authentication = (async () => {
      const user = await api<CurrentUser>('/api/user');
      if ($accessToken.get() !== accessToken) {
        return;
      }
      hydrateUserConfig(user.config);
      $currentUser.set(user);
    })();
    authenticationRef.current = { accessToken, promise: authentication };
    const clearAuthentication = (): void => {
      if (authenticationRef.current?.promise === authentication) {
        authenticationRef.current = undefined;
      }
    };
    void authentication.then(clearAuthentication, clearAuthentication);
    return authentication;
  }, [api, showToast]);
  useEffect(() => {
    if (accessToken && !currentUser) {
      void authenticate().catch(() => undefined);
    }
  }, [accessToken, authenticate, currentUser]);
  useEffect(() => {
    void checkBangumiToken();
  }, [checkBangumiToken, currentUser?.bgmToken, currentUser?.id]);
  useEffect(() => {
    if (!currentUser) {
      return;
    }
    return configureUserConfigPersistence(
      async (config) => {
        await api<CurrentUser>('/api/user', {
          method: 'PATCH',
          body: JSON.stringify({ config }),
        });
      },
      (reason) => showToast(reason instanceof Error ? reason.message : String(reason), 'error'),
    );
  }, [api, currentUser, showToast]);
  const navigate = useCallback((path: string, replace = false) => {
    if (replace) {
      history.replaceState(null, '', path);
    } else {
      history.pushState(null, '', path);
    }
    setRoute(readRoute());
    scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const changeLocale = useCallback((next: Locale) => {
    setLocale(next);
    document.documentElement.lang = next;
    setLocaleState(next);
  }, []);
  const context = useMemo(
    () => ({
      api,
      bgm,
      bgmAuth,
      checkBangumiToken,
      locale,
      changeLocale,
      navigate,
      showToast,
    }),
    [api, bgm, bgmAuth, changeLocale, checkBangumiToken, locale, navigate, showToast],
  );

  return (
    <AppContext.Provider value={context}>
      {accessToken ? <AppLayout route={route} onLogout={logout} /> : <LoginPage onAuthenticated={authenticate} />}
      <div className="toast-list" aria-live="polite">
        {toasts.map((toast) => {
          const ToastIcon = toast.type === 'error' ? CircleAlert : toast.type === 'success' ? CircleCheck : Info;
          return (
            <div
              className={`toast ${toast.type}${toast.persistent ? ' persistent' : ''}`}
              key={toast.id}
              onAnimationEnd={(event) => {
                if (
                  event.target === event.currentTarget &&
                  (event.animationName === 'toast-out' || event.animationName === 'toast-expire')
                ) {
                  removeToast(toast.id);
                }
              }}
            >
              <ToastIcon />
              <span>{toast.message}</span>
              {toast.action ? (
                <Button variant="text" className="toast-action" onClick={toast.action.run}>
                  {toast.action.label}
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>
    </AppContext.Provider>
  );
};

document.documentElement.lang = getLocale();
const root = document.querySelector<HTMLElement>('#root');
if (!root) {
  throw new Error('Missing application root');
}
createRoot(root).render(<App />);
